/* eslint-disable react-refresh/only-export-components */
import { createContext, use, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { authApi, clearActiveOrganizationId, setActiveOrganizationId } from '@/lib/api'
import type { User } from '@/types/api'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  completeLogin: (userData?: User, organizationId?: string) => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  // Check if user is logged in on mount by calling /me.
  // The httpOnly cookie is sent automatically by the browser.
  useEffect(() => {
    authApi
      .getCurrentUser()
      .then((userData) => {
        setUser(userData)
      })
      .catch(() => {
        // Not authenticated or token expired — nothing to clear,
        // cookie is httpOnly so JS can't touch it.
        clearActiveOrganizationId()
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  // The backend sets the httpOnly cookie; the frontend only mirrors session state.
  const completeLogin = useCallback((userData?: User, organizationId?: string) => {
    const organizationFromUser = (userData as (User & { organization_id?: string }) | undefined)?.organization_id
    setActiveOrganizationId(organizationId ?? organizationFromUser)
    if (userData) {
      setUser(userData)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // Ignore logout errors - server clears the cookie anyway
    } finally {
      setUser(null)
      clearActiveOrganizationId()
      navigate('/login')
    }
  }, [navigate])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        completeLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// React 19: use() instead of useContext() (react19-no-forwardref)
export function useAuth() {
  const context = use(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
